import { Injectable, inject } from "@angular/core";
import { tap } from "rxjs";
import { ProfileStoreService } from "./profile-store-service";
import { IProfileModelMap, IProfileModel, Object, IShift, IMapLayer, IGeozone, IIcon, ITrendIcon, IDriverGroup, IReportTemplate, ICommandTemplate, IGeozoneGroup, IDriver, IDriverKeyGroup, IObjectGroup, IDriverPenalty, ISensor, IDriverKey, IGeozoneParsed, ObjectParsed } from "app/system/mobile-models/profile-models";
import { TimeZoneConverterService } from "./time-converter-service";
import { UsersService, UserViewModel } from "app/services/users.service";
import { ProfileApiService } from "./profile-api.service";
import { ObjectStatusService } from "./object-status-service";

@Injectable({ providedIn: 'root' })
export class BaseProfileService {
  private store = inject(ProfileStoreService);
  private api = inject(ProfileApiService);
  public profileMap: IProfileModelMap;
  public serverTimeDiff: number;
  private profile: IProfileModel;
  private user: UserViewModel;
  private converter = inject(TimeZoneConverterService);
  private objectStatusService = inject(ObjectStatusService);
  private usersService = inject(UsersService);

  constructor() {
    this.user = this.usersService.getCurrentUser();
  }

  private arrayToMap<TIn extends { id: number }, TOut = TIn>(
    arr: TIn[],
    mapFn?: (item: TIn) => TOut
  ): Map<number, TOut> {
    return new Map(
      arr.map(item => [item.id, mapFn ? mapFn(item) : (item as unknown as TOut)])
    );
  }

  private convertToMapModel(model: IProfileModel): IProfileModelMap {
    return {
      commandTemplates: this.arrayToMap(model.commandTemplates),
      icons: this.arrayToMap(model.icons),
      trendIcons: this.arrayToMap(model.trendIcons),
      driverGroups: this.arrayToMap(model.driverGroups),
      geozones: this.arrayToMap<IGeozone, IGeozoneParsed>(model.geozones, x => ({
        ...x,
        points: this.converter.parsePoints(x.points),
      })),
      objects: this.arrayToMap<Object, ObjectParsed>(model.objects, x => ({
        ...x,
        createDate: this.converter.toLocal(x.createDate, this.user.TimeZone),
      })),
      shifts: this.arrayToMap(model.shifts),
      geozoneGroups: this.arrayToMap(model.geozoneGroups),
      drivers: this.arrayToMap(model.drivers),
      driverKeys: this.arrayToMap(model.driverKeys),
      driverKeysGroup: this.arrayToMap(model.driverKeysGroup),
      objectGroups: this.arrayToMap(model.objectGroups),
      driverPenalty: model.driverPenalty,
      reportTemplates: this.arrayToMap(model.reportTemplates),
      mapLayers: this.arrayToMap(model.mapLayers),
      serverTime: model.serverTime,
      flags: model.flags,
      geocoderMaxRequest: model.geocoderMaxRequest,
    };
  }

  public getObjects(): Object[] { return this.profile?.objects ?? []; }
  public getShifts(): IShift[] { return this.profile.shifts; }
  public getMapLayers(): IMapLayer[] { return this.profile.mapLayers; }
  public getGeozones(): IGeozoneParsed[] { return Array.from(this.profileMap.geozones.values()); }
  public getIcons(): IIcon[] { return this.profile.icons; }
  public getTrendIcons(): ITrendIcon[] { return this.profile.trendIcons; }
  public getDriverGroups(): IDriverGroup[] { return this.profile.driverGroups; }
  public getReportTemplates(): IReportTemplate[] { return this.profile.reportTemplates; }
  public getCommandTemplates(): ICommandTemplate[] { return this.profile.commandTemplates; }
  public getGeozoneGroups(): IGeozoneGroup[] { return this.profile.geozoneGroups; }
  public getDrivers(): IDriver[] { return this.profile.drivers; }
  public getDriverKeys(): IDriverKey[] { return this.profile.driverKeys; }
  public getDriverKeysGroup(): IDriverKeyGroup[] { return this.profile.driverKeysGroup; }
  public getObjectGroups(): IObjectGroup[] { return this.profile.objectGroups; }

  public getDriverPenalty(): IDriverPenalty { return this.profile.driverPenalty; }
  public getFlags(): string { return this.profile.flags; }
  public getGeocoderMaxRequest(): number { return this.profile.geocoderMaxRequest; }
  public getApplicationVersion(): string { return this.profile.appVersion; }

  public getProfileJson() {
    return this.api.getProfile().pipe(
      tap(result => {
        this.profile = result;
        this.profileMap = this.convertToMapModel(result);
        let now = (new Date()).getTime();
        this.serverTimeDiff = now - this.profileMap.serverTime;
      })
    );
  }

  public getObjectById(id: number): ObjectParsed | null {
    return this.profileMap.objects.get(id) ?? null;
  }

  public getServerTime() {
    var now = new Date();
    return now.getTime() - this.serverTimeDiff;
  }

  public getIconByObjectId(id: number): IIcon | undefined {
    const obj = this.getObjectById(id);
    return obj ? this.getIconUrlById(obj.iconId) : undefined;
  }

  public getIconUrlById(id: number): IIcon | undefined {
    return this.profileMap?.icons.get(id);
  }

  public getSensorByItem(objectId: number, item: string): ISensor | null {
    const obj = this.getObjectById(objectId);
    if (!obj?.sensors) return null;
    return obj.sensors.find(s => s.source === item) ?? null;
  }

  public objectHasFlag(objectid: number, flag: string) {
    return this.getObjectById(objectid).flags.indexOf(flag) >= 0;
  }

  public getSensorsByObjectId(objectId: number, subst?: string | string[]): ISensor[] {
    const obj = this.getObjectById(objectId);
    const sensors = obj?.sensors ?? [];
    if (!subst || (Array.isArray(subst) && subst.length === 0)) return [...sensors];
    const flags = Array.isArray(subst) ? subst : [subst];
    return sensors.filter(s => flags.some(f => this.sensorHasFlag(s, f)));
  }

  public sensorHasFlag(sensor: ISensor, flag: string) { return sensor.flags.indexOf(flag) >= 0; }

  public getIconSrc(objectId: number): string {
    const icon = this.getIconByObjectId(objectId);
    const obj = this.getObjectById(objectId);
    if (!icon || !obj) return '';

    const pos = this.objectStatusService.currentStatus?.positions.get(objectId);
    if (pos) {
      // Example: handle inactivity by timeout
      const unixSec = Math.floor(pos.time.getTime() / 1000);

      if (unixSec < this.getServerTime() - obj.timeout) {
        return icon.urlInactive ?? icon.url;
      }


      // Future TODOs (event/alarm logic) can live here cleanly
      // const alarm = this.profile.getSensorByItem(objectId, 'ALARM');
      // if (alarm && icon.urlEvent) { /* ... */ }
    }

    return icon.url ?? '';
  }

  public getAltitude(objectId: number): number {
    const pos = this.objectStatusService.currentStatus?.positions.get(objectId);
    return pos.alt;
  }

  public isObjectActive(objectId: number): boolean {
    const obj = this.getObjectById(objectId);
    const pos = this.objectStatusService.currentStatus?.positions.get(objectId);

    if (!obj || !pos) return false;

    const posTimeMs = pos.time.getTime();              // already milliseconds
    const serverTimeMs = this.getServerTime();         // milliseconds
    const timeoutMs = obj.timeout * 1000;              // convert seconds → ms

    return posTimeMs >= serverTimeMs - timeoutMs;
  }
}
